import { useState } from 'react';
import classNames from 'classnames';
import { components } from 'react-select';
import { Payment, RefundReason } from '../../clients/quipli-service/payment/payment';
import { usePaymentApiV2 } from '../../clients/quipli-service/payment/payment-api-v2';
import { displayMoneyFormat } from '../../util/util';
import { toast } from '../../util/toasts';
import { FiltersModal } from '../../components/modal/filters-modal';
import { Typography } from '../../components/typography/typography';
import { SelectForm } from '../../components/select-form/select-form';

export const RefundStatementPaymentModal = ({
  isOpen,
  close,
  currency,
  payment,
}: {
  isOpen: boolean;
  close: (payments?: Payment[]) => void;
  currency: string;
  payment: Payment;
}) => {
  const { refundStatement } = usePaymentApiV2();

  const refundReasonsOptions = Object.values(RefundReason).map((r) => {
    return { id: r, label: r };
  });
  const maxRefund = payment.availableAmount ?? payment.amount;
  const [amount, setAmount] = useState<number>(payment.availableAmount || 0);
  const [selectedReason, setSelectedReason] = useState<string>(refundReasonsOptions[0].id);
  const [isCapturing, setIsCapturing] = useState<boolean>(false);
  const formattedAmount = displayMoneyFormat(amount, currency);

  async function doRefund() {
    if (amount && selectedReason) {
      setIsCapturing(true);
      try {
        const result = await refundStatement(payment.userId, {
          amount: amount,
          reason: Object.keys(RefundReason)[
            Object.values(RefundReason).indexOf(
              refundReasonsOptions.find((r) => r.id === selectedReason)?.id || RefundReason.Other,
            )
          ] as RefundReason,
          paymentId: payment.id,
        });

        toast('Refund Successful');
        close([result]);
      } catch (ex: any) {
        toast('Error refunding invoice', 'error');
      } finally {
        setIsCapturing(false);
      }
    }
  }

  return (
    <FiltersModal
      isOpen={isOpen}
      title={`Refund Statement Payment`}
      confirmText={`Refund ${formattedAmount}`}
      cancelText="Cancel"
      allowConfirm={!isCapturing && maxRefund >= amount && amount > 0}
      allowCancel={!isCapturing}
      showConfirmButtonFirst={false}
      onConfirm={() => {
        doRefund();
      }}
      onCancel={() => {
        close();
      }}
    >
      <div className="min-w-full mb-[24px] border border-solid border-[var(--grey-200)] rounded-lg flex flex-col gap-2"></div>
      <div className="min-w-full flex flex-col gap-2 items-stretch">
        <Typography type="text-xl">Issue Refund</Typography>
        <div className="min-w-full h-[44px] p-[10px] border border-solid border-[var(--grey-300)] rounded-lg flex-[0_1_100%] flex items-center">
          <span className="ml-1 quipli-v2 text-md flex-[0_1_5%] ">$</span>
          <input
            type="number"
            step="0.01"
            className="appearance-textfield !outline-none !border-0 !border-none quipli-v2 text-md flex-[0_1_95%]"
            value={amount}
            onChange={(e) => setAmount(+e.target.value)}
          />
        </div>
        {maxRefund < amount && (
          <Typography type="text-sm" weight="regular" className="text-[var(--error-500)]">
            Refund amount cannot exceed {displayMoneyFormat(maxRefund, currency)}
          </Typography>
        )}

        <Typography type="text-md" weight="medium" className="text-[var(--grey-700)] mt-[16px]">
          Reason
        </Typography>
        <SelectForm
          options={refundReasonsOptions}
          value={refundReasonsOptions.find((r) => r.id === selectedReason)}
          isSearchable={false}
          className={classNames('w-[380px] h-[44px]')}
          getOptionLabel={(r) => r.label}
          onChange={(r) => setSelectedReason(r?.id as string)}
          components={{
            SingleValue: (props) => (
              <components.SingleValue {...props}>
                <Typography type="text-md" className="text-[var(--grey-800)]">
                  {props.children}
                </Typography>
              </components.SingleValue>
            ),
          }}
        />
      </div>
    </FiltersModal>
  );
};
